import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

const TABS = [
  { key: "agency", labelKey: "admin_approvals.tab_agency" },
  { key: "employer", labelKey: "admin_approvals.tab_employer" },
];

const FILTERS = [
  { key: "pending_approval", labelKey: "admin_agencies.filter_pending" },
  { key: "active", labelKey: "admin_agencies.filter_active" },
  { key: "rejected", labelKey: "admin_agencies.filter_rejected" },
  { key: "all", labelKey: "admin_agencies.filter_all" },
];

function StatusPill({ status }) {
  const { t } = useTranslation();
  const map = {
    pending_approval: "bg-amber-50 text-amber-700 ring-amber-100",
    active: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    rejected: "bg-rose-50 text-rose-700 ring-rose-100",
    suspended: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ring-1 ${map[status] || map.suspended}`} data-testid={`status-pill-${status}`}>
      {t(`admin_agencies.status_${status}`)}
    </span>
  );
}

export default function AdminApprovals() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("agency");
  const [filter, setFilter] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { org_type: tab };
      if (filter !== "all") params.status = filter;
      else params.status = ""; // explicitly clear
      const { data } = await api.get("/admin/approvals", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  }, [tab, filter, t]);

  useEffect(() => { load(); }, [load]);

  const act = async (orgId, action) => {
    setBusyId(orgId);
    try {
      await api.post(`/admin/orgs/${orgId}/${action}`);
      toast.success(action === "approve" ? t("admin_approvals.approved_toast") : t("admin_approvals.rejected_toast"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setBusyId(null); }
  };

  const rows = useMemo(() => items, [items]);
  const isAgencyTab = tab === "agency";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-approvals-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_approvals.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-testid="admin-approvals-title">
            {t("admin_approvals.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("admin_approvals.subtitle")}</p>
        </div>

        {/* Tabs */}
        <div className="mb-4 flex gap-1 border-b border-slate-200" data-testid="admin-approvals-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              data-testid={`tab-${tb.key}`}
              className={
                "-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors " +
                (tab === tb.key
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-700")
              }
            >
              {t(tb.labelKey)}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-approvals-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={
                "rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
                (filter === f.key
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100")
              }
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">{isAgencyTab ? t("admin_agencies.col_agency") : t("admin_approvals.col_company")}</th>
                  {isAgencyTab && <th className="px-4 py-3">{t("admin_agencies.col_siret")}</th>}
                  {isAgencyTab && <th className="px-4 py-3">{t("admin_agencies.col_website")}</th>}
                  {isAgencyTab && <th className="px-4 py-3">{t("admin_agencies.col_specialties")}</th>}
                  <th className="px-4 py-3">{t("admin_agencies.col_contact")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_submitted")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_status")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={isAgencyTab ? 8 : 5} className="px-4 py-10 text-center text-slate-500">{t("common.loading")}</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={isAgencyTab ? 8 : 5} className="px-4 py-10 text-center text-slate-500" data-testid="admin-approvals-empty">{t("admin_agencies.empty")}</td></tr>
                )}
                {!loading && rows.map((it) => (
                  <tr key={it.organization.id} data-testid={`approval-row-${it.organization.id}`} className="align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{it.organization.name}</div>
                      {it.organization.application_notes && (
                        <div className="mt-1 max-w-xs text-xs text-slate-500">
                          <span className="font-semibold text-slate-600">{t("admin_agencies.notes_label")}:</span> {it.organization.application_notes}
                        </div>
                      )}
                    </td>
                    {isAgencyTab && <td className="px-4 py-3 text-slate-700">{it.organization.siret || "—"}</td>}
                    {isAgencyTab && (
                      <td className="px-4 py-3">
                        {it.organization.website ? (
                          <a href={it.organization.website} target="_blank" rel="noreferrer" className="text-indigo-700 hover:text-indigo-800">{it.organization.website}</a>
                        ) : "—"}
                      </td>
                    )}
                    {isAgencyTab && (
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(it.organization.specialties || []).map((s) => (
                            <span key={s} className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{s}</span>
                          ))}
                          {(!it.organization.specialties || it.organization.specialties.length === 0) && "—"}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-700">
                      <div>{it.applicant_name}</div>
                      <div className="text-xs text-slate-500">{it.applicant_email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{formatDateShort(it.submitted_at)}</td>
                    <td className="px-4 py-3"><StatusPill status={it.organization.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => act(it.organization.id, "approve")}
                          disabled={busyId === it.organization.id || it.organization.status === "active"}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                          data-testid={`approve-btn-${it.organization.id}`}
                        >
                          {t("admin_agencies.approve")}
                        </button>
                        <button
                          onClick={() => act(it.organization.id, "reject")}
                          disabled={busyId === it.organization.id || it.organization.status === "rejected"}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          data-testid={`reject-btn-${it.organization.id}`}
                        >
                          {t("admin_agencies.reject")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
